import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "@/context/LangContext";

const PRODUCT_1L = "https://customer-assets-39nsmqrw.emergentagent.net/job_pakla-wash/artifacts/2pwa5vhg_pakla_1L_visual.webp";
const SPLASH_IMG = "https://customer-assets-39nsmqrw.emergentagent.net/job_pakla-wash/artifacts/81aiw6zv_image.png";

const lineVariant = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: {
      delay: 0.35 + i * 0.14,
      duration: 1.05,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bottleY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const splashY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.2]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Background flourish */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#2E8B4A]/8 blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full bg-[#2E7CD6]/8 blur-3xl" />
      </div>

      <motion.div style={{ opacity: heroOpacity }} className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="flex items-center gap-4 font-eyebrow text-2xl md:text-[30px] text-[#0F3D2E]/85"
          data-testid="hero-eyebrow"
        >
          <span className="w-10 h-px bg-[#0F3D2E]/50" />
          {t.hero.eyebrow}
        </motion.div>

        <div className="grid grid-cols-12 gap-6 md:gap-8 mt-8 md:mt-12">
          {/* Left: kinetic headline + copy */}
          <div className="col-span-12 lg:col-span-7">
            <h1
              className="font-serif-editorial text-[#0F3D2E] leading-[0.92] tracking-tight text-[15vw] sm:text-[13vw] md:text-[10vw] lg:text-[8.2vw] xl:text-[128px]"
              data-testid="hero-headline"
            >
              {t.hero.lines.map((line, i) => (
                <span key={i} className="block mask-reveal align-baseline">
                  <motion.span
                    className="inline-block will-change-transform"
                    variants={lineVariant}
                    initial="hidden"
                    animate="visible"
                    custom={i}
                  >
                    {i === t.hero.lines.length - 1 ? (
                      <span>
                        <em className="italic font-[500] text-[#2E8B4A]">{line.split(" ")[0]}</em>{" "}
                        {line.split(" ").slice(1).join(" ")}
                      </span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 md:mt-10 max-w-xl text-[15px] md:text-[17px] leading-relaxed text-[#0A1F17]/75"
              data-testid="hero-body"
            >
              {t.hero.body}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.35, duration: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#howto"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 h-12 px-6 rounded-full bg-[#0F3D2E] text-[#FAF7F0] text-[13px] tracking-[0.18em] uppercase hover:bg-[#2E8B4A] transition-colors"
              >
                {t.hero.primary}
                <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-[#FAF7F0]/15 group-hover:bg-[#FAF7F0]/25 transition-colors">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6h8m0 0L7 3m3 3L7 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </a>
              <a
                href="#contact"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-3 h-12 px-6 rounded-full border border-[#0F3D2E]/40 text-[#0F3D2E] text-[13px] tracking-[0.18em] uppercase hover:border-[#0F3D2E] hover:bg-[#0F3D2E]/5 transition-colors"
              >
                {t.hero.secondary}
              </a>
            </motion.div>
          </div>

          {/* Right: produce image only */}
          <div className="col-span-12 lg:col-span-5 relative min-h-[420px] md:min-h-[520px] lg:min-h-[560px] lg:-mt-16 lg:mb-4">
            <motion.div
              style={{ y: splashY }}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
              data-testid="hero-splash-frame"
            >
              <img
                src={SPLASH_IMG}
                alt="Fresh assorted fruits and vegetables"
                className="w-full h-full object-cover rounded-2xl md:rounded-3xl"
                draggable="false"
              />
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
